import React, { useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  CheckoutElementsProvider,
  PaymentElement,
  useCheckoutElements,
} from '@stripe/react-stripe-js/checkout';
import Button from '../../../components/common/Button/Button';
import config from '../../../config/environment';
import { CHECKOUT_STRIPE_PUBLIC_ERROR_MESSAGE } from '../checkout.constants';
import { toPublicCheckoutErrorMessage } from '../checkout.utils';
import styles from './CheckoutShared.module.css';

interface StripePaymentFormProps {
  disabled: boolean;
  onSubmittingChange: (submitting: boolean) => void;
  onError: (message: string) => void;
  onLoadFailed?: () => void;
  onReady?: () => void;
  onConfirmed: () => void;
}

const StripePaymentForm: React.FC<StripePaymentFormProps> = ({
  disabled,
  onSubmittingChange,
  onError,
  onLoadFailed,
  onReady,
  onConfirmed,
}) => {
  const checkoutState = useCheckoutElements();
  const [isConfirming, setIsConfirming] = useState(false);
  const reportedLoadErrorRef = useRef(false);

  useEffect(() => {
    if (checkoutState.type === 'loading' || checkoutState.type === 'error') {
      return;
    }

    onReady?.();
  }, [checkoutState.type, onReady]);

  useEffect(() => {
    if (checkoutState.type !== 'error' || reportedLoadErrorRef.current) {
      return;
    }

    reportedLoadErrorRef.current = true;
    onError(toPublicCheckoutErrorMessage(checkoutState.error.message));
    onLoadFailed?.();
  }, [checkoutState, onError, onLoadFailed]);

  if (checkoutState.type === 'loading') {
    return null;
  }

  if (checkoutState.type === 'error') {
    return <p className={styles.stripeError}>{CHECKOUT_STRIPE_PUBLIC_ERROR_MESSAGE}</p>;
  }

  const handleConfirm = async () => {
    setIsConfirming(true);
    onSubmittingChange(true);
    onError('');

    try {
      const result = await checkoutState.checkout.confirm();
      if (result && result.type === 'error') {
        onError(toPublicCheckoutErrorMessage(result.error.message));
        return;
      }

      onConfirmed();
    } catch {
      onError('Não foi possível confirmar o pagamento no Stripe. Tente novamente.');
    } finally {
      setIsConfirming(false);
      onSubmittingChange(false);
    }
  };

  return (
    <div className={styles.stripePayment}>
      <PaymentElement />
      <Button
        type="button"
        variant="primary"
        size="app"
        fullWidth
        disabled={disabled || isConfirming}
        onClick={() => {
          void handleConfirm();
        }}
      >
        {isConfirming ? 'Confirmando...' : 'Finalizar contratação'}
      </Button>
    </div>
  );
};

interface StripePaymentElementProps {
  clientSecret: string;
  disabled?: boolean;
  onSubmittingChange: (submitting: boolean) => void;
  onError: (message: string) => void;
  onLoadFailed?: () => void;
  onReady?: () => void;
  onConfirmed: () => void;
}

const StripePaymentElement: React.FC<StripePaymentElementProps> = ({
  clientSecret,
  disabled = false,
  onSubmittingChange,
  onError,
  onLoadFailed,
  onReady,
  onConfirmed,
}) => {
  const stripePromise = useMemo(
    () => (
      config.stripePublishableKey
        ? loadStripe(config.stripePublishableKey)
        : Promise.resolve(null)
    ),
    [],
  );

  if (!config.stripePublishableKey) {
    return (
      <p className={styles.stripeError}>
        O pagamento internacional está temporariamente indisponível.
      </p>
    );
  }

  return (
    <CheckoutElementsProvider
      stripe={stripePromise}
      options={{
        clientSecret,
        elementsOptions: {
          appearance: {
            theme: 'stripe',
          },
        },
      }}
    >
      <StripePaymentForm
        disabled={disabled}
        onSubmittingChange={onSubmittingChange}
        onError={onError}
        onLoadFailed={onLoadFailed}
        onReady={onReady}
        onConfirmed={onConfirmed}
      />
    </CheckoutElementsProvider>
  );
};

export default StripePaymentElement;
